import { useState, useReducer, useEffect, useCallback, useRef } from 'react';
import { api } from '../api/api';
import { useAuth } from '../auth/AuthContext';
import { deriveDefaultsFromGlobals } from '../utils/defaults';

const historyReducer = (state, action) => {
  const { past, present, future } = state;
  switch (action.type) {
    case 'SET_STATE':
      if (action.payload === present) return state;
      return { past: [...past, present], present: action.payload, future: [] };
    case 'UNDO':
      if (past.length === 0) return state;
      const previous = past[past.length - 1];
      return {
        past: past.slice(0, past.length - 1),
        present: previous,
        future: [present, ...future],
      };
    case 'REDO':
      if (future.length === 0) return state;
      const next = future[0];
      return {
        past: [...past, present],
        present: next,
        future: future.slice(1),
      };
    case 'RESET':
      return { past: [], present: action.payload || {}, future: [] };
    default:
      return state;
  }
};


export const useConfiguratorState = () => {
  const { user, logout } = useAuth();
  const [initialState, setInitialState] = useState(null);
  const [draftMeta, setDraftMeta] = useState({ templateId: null, updatedAt: null });
  const saveTimeoutRef = useRef(null);
  const lastSyncedAtRef = useRef(null);
  const isMountedRef = useRef(false);
  const logoutRef = useRef(logout);

  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const loadInitialData = async () => {
      if (!user) {
        setInitialState(null);
        setDraftMeta({ templateId: null, updatedAt: null });
        return;
      }

      const storageKey = `currentConfig:${user.id}`;
      try {
        const draft = await api.getConfiguratorDraft();
        if (
          draft &&
          draft.data &&
          typeof draft.data === 'object' &&
          isMountedRef.current
        ) {
          setInitialState(draft.data);
          setDraftMeta({
            templateId:
              typeof draft.templateId === 'number' ? draft.templateId : null,
            updatedAt: draft.updatedAt || null,
          });
          lastSyncedAtRef.current = draft.updatedAt || null;
          try {
            sessionStorage.setItem(storageKey, JSON.stringify(draft.data));
          } catch {
            // ignore storage failures
          }
          return;
        }
      } catch (error) {
        console.warn('Failed to load configurator draft', error);
        if (error?.status === 401) {
          if (typeof logoutRef.current === 'function') {
            logoutRef.current();
          }
          return;
        }
      }

      const savedConfig = sessionStorage.getItem(storageKey);
      if (savedConfig) {
        try {
          const parsed = JSON.parse(savedConfig);
          setInitialState(parsed);
          setDraftMeta({ templateId: null, updatedAt: null });
          lastSyncedAtRef.current = null;
          return;
        } catch (e) {
          console.error('Could not parse saved config, clearing it.', e);
          sessionStorage.removeItem(storageKey);
        }
      }

      try {
        const globalSettings = await api.getGlobalSettings();
        if (!isMountedRef.current) return;
        const mergedTemplate = deriveDefaultsFromGlobals(globalSettings);
        setInitialState(mergedTemplate);
        setDraftMeta({ templateId: null, updatedAt: null });
        lastSyncedAtRef.current = null;
      } catch (error) {
        console.error('Failed to load global settings.', error);
        if (error?.status === 401) {
          if (typeof logoutRef.current === 'function') {
            logoutRef.current();
          }
          return;
        }
        if (isMountedRef.current) {
          setInitialState(deriveDefaultsFromGlobals(null));
          setDraftMeta({ templateId: null, updatedAt: null });
          lastSyncedAtRef.current = null;
        }
      }
    };

    loadInitialData();
  }, [user]);

  const [state, dispatch] = useReducer(historyReducer, {
    past: [],
    present: initialState,
    future: [],
  });

  useEffect(() => {
    if (initialState) {
      dispatch({ type: 'RESET', payload: initialState });
    }
  }, [initialState]);

  useEffect(() => {
    if (state.present) {
      const key = user ? `currentConfig:${user.id}` : 'currentConfig:anon';
      try {
        sessionStorage.setItem(key, JSON.stringify(state.present));
      } catch (err) {
        console.warn('Failed to persist configurator state locally', err);
      }
    }
  }, [state.present, user]);

  useEffect(() => {
    if (!user || !state.present) return undefined;
    const handler = setTimeout(async () => {
      try {
        const draft = await api.saveConfiguratorDraft({
          data: state.present,
          templateId: draftMeta.templateId,
          lastSyncedAt: lastSyncedAtRef.current,
        });
        if (!isMountedRef.current) return;
        const updatedAt = draft?.updatedAt || new Date().toISOString();
        lastSyncedAtRef.current = updatedAt;
        setDraftMeta(prev => ({
          templateId:
            typeof draft?.templateId === 'number'
              ? draft.templateId
              : prev.templateId,
          updatedAt,
        }));
      } catch (error) {
        if (error?.status === 409 && error.payload?.draft) {
          const serverDraft = error.payload.draft;
          lastSyncedAtRef.current = serverDraft.updatedAt || null;
          if (isMountedRef.current) {
            setDraftMeta(prev => ({
              templateId:
                typeof serverDraft.templateId === 'number'
                  ? serverDraft.templateId
                  : prev.templateId,
              updatedAt: serverDraft.updatedAt || null,
            }));
          }
          console.warn('Draft conflict detected; keeping server revision.');
          return;
        }
        console.warn('Failed to persist configurator draft to server', error);
      }
    }, 800);
    saveTimeoutRef.current = handler;
    return () => {
      clearTimeout(handler);
      if (saveTimeoutRef.current === handler) {
        saveTimeoutRef.current = null;
      }
    };
  }, [state.present, user, draftMeta.templateId]);

  // Simple beforeunload warning for tab closure only
  useEffect(() => {
    const handleBeforeUnload = e => {
      // Only show warning if there are actual settings (not just the default state)
      if (
        state.present &&
        Object.keys(state.present).length > 0 &&
        state.present.id !== 'none'
      ) {
        const message =
          'You have unsaved changes to your email template configuration. Are you sure you want to leave this page? Your changes will be lost.';
        e.preventDefault();
        e.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [state.present]);

  const setSettings = useCallback(
    newSettings => {
      const payload =
        typeof newSettings === 'function'
          ? newSettings(state.present)
          : newSettings;
      dispatch({ type: 'SET_STATE', payload });
    },
    [state.present]
  );

  const resetState = useCallback(
    newState => {
      dispatch({ type: 'RESET', payload: newState || initialState });
    },
    [initialState]
  );

  const undo = () => dispatch({ type: 'UNDO' });
  const redo = () => dispatch({ type: 'REDO' });

  return {
    settings: state.present,
    setSettings,
    resetState,
    undo,
    redo,
    canUndo: state.past.length > 0,
    canRedo: state.future.length > 0,
    // Expose the initial/default state so callers can reset sections
    initialState,
    draftMeta,
  };
};
